<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <!-- Header -->
    <v-row align="center" class="mb-5">
      <v-col cols="12" class="d-flex align-center">
        <v-spacer />
        <v-btn
          v-if="gstConfig"
          small
          class="accent white--text"
          @click="openDialog"
        >
          <v-icon left small>mdi-pencil</v-icon>
          Update
        </v-btn>
      </v-col>
    </v-row>

    <!-- Loading -->
    <div v-if="loading" class="d-flex justify-center align-center py-12">
      <v-progress-circular indeterminate color="accent" size="36" />
    </div>

    <!-- Configured state -->
    <template v-else-if="gstConfig">
      <!-- Rate cards -->
      <v-row class="mb-3">
        <v-col cols="12" sm="6">
          <v-sheet outlined rounded class="pa-5 text-center">
            <div
              class="caption text--secondary mb-1 text-uppercase letter-spacing-1"
            >
              CGST
            </div>
            <div class="text-h4 font-weight-bold accent--text">
              {{ gstConfig.cgst }}<span class="text-h6">%</span>
            </div>
            <div class="caption grey--text mt-1">Central GST</div>
          </v-sheet>
        </v-col>

        <v-col cols="12" sm="6">
          <v-sheet outlined rounded class="pa-5 text-center">
            <div
              class="caption text--secondary mb-1 text-uppercase letter-spacing-1"
            >
              SGST
            </div>
            <div class="text-h4 font-weight-bold accent--text">
              {{ gstConfig.sgst }}<span class="text-h6">%</span>
            </div>
            <div class="caption grey--text mt-1">State GST</div>
          </v-sheet>
        </v-col>
        <!--
        <v-col cols="12" sm="4">
          <v-sheet outlined rounded class="pa-5 text-center">
            <div
              class="caption text--secondary mb-1 text-uppercase letter-spacing-1"
            >
              IGST
            </div>
            <div class="text-h4 font-weight-bold accent--text">
              {{ gstConfig.igst }}<span class="text-h6">%</span>
            </div>
            <div class="caption grey--text mt-1">Integrated GST</div>
          </v-sheet>
        </v-col>-->
      </v-row>

      <!-- Intra-state summary + particular -->
      <v-sheet
        outlined
        rounded
        class="pa-3 mb-5 d-flex align-center flex-wrap"
        style="gap: 24px"
      >
        <div>
          <span class="caption grey--text text--darken-1"
            >Particular : &nbsp;</span
          >
          <span class="text-caption">{{ gstConfig.particular }}</span>
        </div>
      </v-sheet>

      <!-- History -->
      <template v-if="gstConfig.history && gstConfig.history.length">
        <div
          class="subtitle-2 font-weight-semibold mb-3 grey--text text--darken-2"
        >
          Change History
        </div>

        <v-sheet outlined rounded class="pa-0 overflow-hidden mb-2">
          <v-simple-table dense>
            <thead>
              <tr>
                <th class="caption">#</th>
                <th class="caption">CGST</th>
                <th class="caption">SGST</th>
                <!--<th class="caption">IGST</th>-->
                <th class="caption">Changed At</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(entry, i) in visibleHistory"
                :key="i"
                :class="i % 2 === 0 ? '' : 'grey lighten-5'"
              >
                <td class="caption grey--text">{{ i + 1 }}</td>
                <td class="caption">{{ entry.cgst }}%</td>
                <td class="caption">{{ entry.sgst }}%</td>
                <!--<td class="caption">{{ entry.igst }}%</td>-->
                <td class="caption grey--text">
                  {{ formatDateTime(entry.updated_at) }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-sheet>

        <div
          v-if="gstConfig.history.length > historyPageSize"
          class="text-center mt-1 mb-4"
        >
          <v-btn
            text
            x-small
            color="accent"
            @click="showAllHistory = !showAllHistory"
          >
            {{
              showAllHistory
                ? "Show less"
                : `Show ${gstConfig.history.length - historyPageSize} more…`
            }}
          </v-btn>
        </div>
      </template>

      <v-sheet
        v-else
        outlined
        rounded
        class="d-flex align-center pa-4 mb-4"
        style="gap: 10px"
      >
        <v-icon color="grey lighten-2">mdi-history</v-icon>
        <span class="caption grey--text"
          >No previous changes recorded yet.</span
        >
      </v-sheet>
    </template>

    <!-- Empty state -->
    <v-sheet
      v-else
      rounded
      class="d-flex flex-column align-center justify-center pa-12"
    >
      <div
        class="text-subtitle-1 font-weight-medium grey--text text--darken-1 mb-1"
      >
        No GST configuration set
      </div>
      <div class="caption grey--text mb-5">
        Configure CGST and SGST rates to apply on transactions
      </div>
      <v-btn small class="accent white--text" @click="openDialog">
        <v-icon left small>mdi-plus</v-icon>
        Configure Now
      </v-btn>
    </v-sheet>

    <!-- Form dialog -->
    <v-dialog v-model="dialog" max-width="480" persistent>
      <GSTConfigurationForm
        v-if="dialog"
        :gst-configuration="gstConfig"
        @closeDialog="dialog = false"
        @saved="onSaved"
        :payment-types="paymentTypes"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import GSTConfigurationForm from "@/components/gst/form.vue";

export default {
  components: { GSTConfigurationForm },

  data() {
    return {
      gstConfig: null,
      dialog: false,
      loading: false,
      showAllHistory: false,
      historyPageSize: 5,
      paymentTypes: [{ id: "Add-on Programme", name: "Add-on Programme" }],
    };
  },

  computed: {
    intraStateTotal() {
      if (!this.gstConfig) return 0;
      return (parseFloat(this.gstConfig.cgst) + parseFloat(this.gstConfig.sgst))
        .toFixed(2)
        .replace(/\.00$/, "");
    },

    visibleHistory() {
      if (!this.gstConfig?.history) return [];
      return this.showAllHistory
        ? this.gstConfig.history
        : this.gstConfig.history.slice(0, this.historyPageSize);
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "GST Configuration" }]);
    this.fetchConfig();
  },

  methods: {
    async fetchConfig() {
      this.loading = true;
      try {
        const res = await this.$axios.get("/gst_configuration");
        this.gstConfig = res.data.data;
      } catch (e) {
        console.log(e);
        this.gstConfig = null;
      } finally {
        this.loading = false;
      }
    },

    openDialog() {
      this.dialog = true;
    },

    onSaved(config) {
      this.gstConfig = config;
      this.dialog = false;
    },

    formatDateTime(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
  },
};
</script>
